import { DataType } from "@superego/schema";
import { useState } from "react";
import { useController } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import classnames from "../../../../../utils/classnames.js";
import {
  FieldError,
  TextArea,
  TextField,
} from "../../../../design-system/forms/forms.js";
import AnyFieldLabel from "../../AnyFieldLabel.js";
import * as cs from "../../RHFContentField.css.js";
import { useUiOptions } from "../../uiOptions.js";
import useFieldUiOptions from "../../useFieldUiOptions.js";
import type Props from "../Props.js";

export default function Default({
  typeDefinition,
  isNullable,
  isListItem,
  control,
  name,
  label,
  autoFocus,
}: Props) {
  const { isReadOnly } = useUiOptions();
  const { flexGrow } = useFieldUiOptions(name);
  const {
    field: { ref: fieldRef, ...field },
    fieldState,
  } = useController({ control, name });
  const [jsonValue, setJsonValue] = useState(() =>
    getJsonValueFromValue(field.value),
  );
  const [previousValue, setPreviousValue] = useState(field.value);
  if (previousValue !== field.value) {
    setPreviousValue(field.value);
    if (field.value === null) {
      setJsonValue("");
    }
  }
  return (
    <TextField
      id={field.name}
      name={field.name}
      value={jsonValue}
      onChange={(newValue) => {
        setJsonValue(newValue);
        if (newValue === "") {
          field.onChange(null);
          return;
        }
        try {
          field.onChange({
            ...JSON.parse(newValue),
            __dataType: DataType.JsonObject,
          });
        } catch {
          field.onChange(newValue);
        }
      }}
      onBlur={() => {
        // Resets jsonValue, so it re-formats.
        setJsonValue(getJsonValueFromValue(field.value));
        field.onBlur();
      }}
      validationBehavior="aria"
      autoComplete="off"
      autoFocus={autoFocus}
      isInvalid={fieldState.invalid}
      isReadOnly={isReadOnly}
      aria-label={isListItem ? label : undefined}
      data-data-type={typeDefinition.dataType}
      data-is-list-item={isListItem}
      data-testid="widgets.RHFContentField.JsonObjectField.Default.root"
      className={classnames(
        isListItem && cs.ListItemField.root,
        flexGrow && cs.Field.flexGrow,
      )}
    >
      {!isListItem ? (
        <AnyFieldLabel
          name={field.name}
          typeDefinition={typeDefinition}
          isNullable={isNullable}
          label={label}
        />
      ) : null}
      <TextArea
        ref={fieldRef}
        placeholder={field.value === null ? "null" : undefined}
        className={classnames(
          cs.JsonObjectField.Default.textArea,
          flexGrow && cs.Field.flexGrowContent,
        )}
      />
      <FieldError>
        {typeof field.value === "string" ? (
          <FormattedMessage defaultMessage="Not a valid JSON string" />
        ) : (
          fieldState.error?.message
        )}
      </FieldError>
    </TextField>
  );
}

function getJsonValueFromValue(value: unknown): string {
  if (typeof value !== "object" || value === null) {
    return "";
  }
  const { __dataType, ...rest } = value as any;
  return JSON.stringify(rest, null, 2);
}
